/** Booking-integration Subscriptions panel: the webhook subscriptions active
 *  for THIS tenant/restech/account (OCTO notifications). Read-mostly — the
 *  set is tiny, so a plain table; management happens on the /subscriptions
 *  grid, reached pre-filtered via the Manage link (or per row: clicking one
 *  opens its detail drawer there).
 */
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import * as subscriptionsApi from '@/api/notificationSubscriptions';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { TsValue } from '@/features/bgtasks/taskFormat';
import { ModeChip, StatusChip } from '@/features/subscriptions/subscriptionFormat';
import {
  subscriptionAccountWireFilters,
  subscriptionsHrefForAccount,
} from '@/features/subscriptions/subscriptionLinks';

import { subscriptionActions } from '../tenantLinks';

const dash = <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>;

interface Props {
  toId: number;
  restech: string;
  /** Account slug; null/undefined = the default account. */
  slug: string | null;
  canManage: boolean;
}

export function SubscriptionsPanel({ toId, restech, slug, canManage }: Props) {
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['subscriptions', 'account-panel', toId, restech, slug ?? ''],
    queryFn: () =>
      subscriptionsApi.list({
        tour_operator_id: toId,
        filters: subscriptionAccountWireFilters(restech, slug),
        page_size: 100,
      }),
  });

  const rows = query.data?.results ?? [];
  const manageHref = subscriptionsHrefForAccount(toId, restech, slug);
  const newHref = `/subscriptions?tenant=${toId}&new=1`;

  const actions = (
    <Stack direction="row" spacing={1}>
      <Button
        size="small"
        startIcon={<OpenInNewIcon />}
        component={RouterLink}
        to={manageHref}
      >
        Manage in Webhook subscriptions
      </Button>
      {canManage && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          component={RouterLink}
          to={newHref}
        >
          New subscription
        </Button>
      )}
    </Stack>
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          Webhook subscriptions for this account
        </Typography>
        {actions}
      </Stack>

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {query.isError && <Alert severity="error">Couldn’t load the subscriptions.</Alert>}

      {query.isSuccess && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No webhook subscriptions exist for this account — the operator is not opted in to any
          notification type. Use New subscription to register one.
        </Typography>
      )}

      {rows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell>Last received</TableCell>
              <TableCell>Last error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow
                key={r.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/subscriptions/${r.id}`)}
              >
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                  {/* The row click still opens the drawer in place; the trigger
                      only adds the new-tab open, so this dialog is not lost. */}
                  <ValueActions actions={subscriptionActions(r)}>{r.notification_type}</ValueActions>
                </TableCell>
                <TableCell>
                  <StatusChip status={r.status} />
                </TableCell>
                <TableCell>
                  <ModeChip mode={r.mode} />
                </TableCell>
                <TableCell>{r.last_received_ts ? <TsValue value={r.last_received_ts} /> : dash}</TableCell>
                <TableCell sx={{ maxWidth: 260 }}>
                  {r.last_error ? (
                    <Tooltip title={r.last_error}>
                      <Box
                        component="span"
                        sx={{
                          color: 'error.main',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {r.last_error}
                      </Box>
                    </Tooltip>
                  ) : (
                    dash
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  );
}
